import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import depotProducts from "@/assets/depot-products.jpg";
import heroProducts from "@/assets/hero-products-shelf.jpg";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ShoppingCart } from "lucide-react";
import ProductQuantityControl from "@/components/ProductQuantityControl";
import SEO from "@/components/SEO";
import { getUserFriendlyError } from "@/lib/errors";
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  active: boolean;
  free_shipping: boolean;
  stripe_price_id: string | null;
}
const Products = () => {
  const introSection = useScrollAnimation();
  const productsSection = useScrollAnimation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  useEffect(() => {
    fetchProducts();
    updateCartCount();
  }, []);
  const fetchProducts = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from("products").select("*").eq("active", true).order("created_at", {
        ascending: false
      });
      if (error) throw error;
      setProducts(data || []);
    } catch (error: unknown) {
      toast({
        title: "Fout bij laden producten",
        description: getUserFriendlyError(error, "Producten laden"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    const total = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
    setCartCount(total);
  };
  return <div className="min-h-screen">
      <SEO
        title="Producten | Bo Barbers Grooming Essentials"
        description="Professionele grooming producten van Bo Barbers en DEPOT. Baardverzorging, styling en haarverzorging — geselecteerd door de barbers."
        path="/products"
        jsonLd={products.length > 0 ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: products.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              name: p.name,
              description: p.description,
              image: p.image_url || undefined,
              offers: {
                "@type": "Offer",
                price: p.price.toFixed(2),
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
              },
            },
          })),
        } : undefined}
      />
      <Header />
      
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <img src={heroProducts} alt="Bo Barbers Products" className="w-full h-full object-cover animate-cinematic-zoom" />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10 py-24 sm:py-28 md:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white/70 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold animate-fade-in" style={{
            animationDelay: "0.2s",
            animationFillMode: "forwards"
          }}>
              bo barbers products   
            </p>
            <h1 style={{
            animationDelay: "0.4s",
            animationFillMode: "forwards"
          }} className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 text-white leading-[0.95] animate-fade-in px-4 xl:text-5xl">
              Grooming essentials
              <br />
              die doen wat ze moeten doen.
            </h1>
            <div className="w-16 sm:w-24 h-1 bg-white mx-auto mb-6 sm:mb-8 animate-fade-in" style={{
            animationDelay: "0.6s",
            animationFillMode: "forwards"
          }}></div>
            <p className="text-white/80 max-w-xl mx-auto text-sm sm:text-base leading-relaxed animate-fade-in px-4" style={{
            animationDelay: "0.8s",
            animationFillMode: "forwards"
          }}>
              Nobelstraat 263, 3512 EM Utrecht
            </p>
            
            {cartCount > 0 && <Button onClick={() => navigate("/cart")} variant="outline" className="hidden sm:inline-flex mt-6 sm:mt-8 border-white text-white hover:bg-white hover:text-foreground rounded-full px-6 sm:px-8 animate-fade-in text-xs sm:text-sm" style={{
            animationDelay: "1s",
            animationFillMode: "forwards"
          }}>
                <ShoppingCart className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                Winkelwagen ({cartCount})
              </Button>}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section ref={introSection.ref} className="py-32 bg-foreground">
        <div className="container mx-auto px-6">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-20 items-center max-w-6xl mx-auto transition-all duration-1000 ${introSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="order-2 lg:order-1">
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-8 text-background leading-[1.1]">
              Creëer je uitstraling met kwaliteit
            </h2>
              <div className="w-16 h-1 bg-background mb-8"></div>
              <p className="text-background/70 mb-6 leading-relaxed text-sm">
                Bij Bo Barbers bieden we een zorgvuldig geselecteerd assortiment professionele verzorgingsproducten. Elk product is met zorg ontwikkeld om je te helpen op je best eruit te zien.
              </p>
              <p className="text-background/70 leading-relaxed text-sm mb-8">
                Van traditionele baardverzorging tot moderne styling producten - wij helpen je met de juiste 
                producten om je grooming routine naar een hoger niveau te tillen.
              </p>
            </div>
            <div className="order-1 lg:order-2 overflow-hidden group">
              <img alt="DEPOT Products" className="w-full h-auto transition-transform duration-700 group-hover:scale-110" src="/lovable-uploads/80c70916-cd3d-45e8-8c49-33d738e8a2bc.jpg" />
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section ref={productsSection.ref} className="py-32 bg-background">
        <div className="container mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${productsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4 text-foreground">
              ONS ASSORTIMENT
            </h2>
            <p className="text-foreground/60 text-sm">Verkrijgbaar bij Bo Barbers</p>
          </div>

          {loading ? <p className="text-center text-foreground/60">Producten laden...</p> : products.length === 0 ? <p className="text-center text-foreground/60">Geen producten beschikbaar</p> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {products.map((product, i) => <Card key={product.id} className={`group overflow-hidden bg-card border-border transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 flex flex-col ${productsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
            transitionDelay: productsSection.isVisible ? `${i * 100}ms` : '0ms'
          }}>
                  <div className="aspect-square bg-muted relative overflow-hidden">
                    <img src={product.image_url || depotProducts} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <h3 className="font-display text-lg font-bold mb-2 text-foreground uppercase tracking-wide">
                      {product.name}
                    </h3>
                    <p className="text-foreground/60 text-sm mb-6 leading-relaxed flex-1">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-2xl font-bold text-foreground tabular-nums">
                        €{product.price.toFixed(2)}
                      </span>
                      <ProductQuantityControl product={product} onCartUpdate={updateCartCount} />
                    </div>
                  </div>
                </Card>)}
            </div>}
        </div>
      </section>

      <Footer />
    </div>;
};
export default Products;